import { breakpointService } from './breakpointService';
import { imageResizerService } from './imageResizerService';
import { mimeTypeService } from './mimeTypeService';
import { conf } from './configService';
import { dateService } from './dateService';
import { stripTags, truncateText } from '../utils/stringUtils';
import { getContentModificationDate } from '../utils/contentUtils';

const getDomain = () =>
  conf.isStage
    ? `http://fng-globalsites-stage-html-current-${conf.siteCountryCode}.s3-website.eu-central-1.amazonaws.com`
    : `https://${conf.canonicalDomain}`;

const getOpenGraphType = bundle => {
  switch (bundle) {
    case 'author':
    case 'photographer':
    case 'error_403':
    case 'error_404':
    case 'homepage':
    case 'hub_page':
    case 'page':
    case 'pod_index':
    case 'search':
    case 'subscribe_page':
    case 'taxonomy':
    case 'television_schedule':
    case 'consent_management':
    case 'newsletter':
    case 'article':
    case 'photo_gallery':
    case 'vertical_slideshow_gallery':
    case 'show':
      return 'article';
    default:
      throw new Error(`Content bundle "${bundle}" unknown.`);
  }
};

export const metaDataServiceHelper = {
  getMetaTitle: (content, metaName, removeSuffix = false) => {
    let title = content.promoTitle || content.title;
    if (metaName && content.metaTags?.[metaName]) {
      title = content.metaTags[metaName];
    } else if (content.metaTags?.title) {
      if (content.metaTags.title.startsWith('| ')) {
        title = `${title} ${content.metaTags.title}`;
      } else {
        ({ title } = content.metaTags);
      }
    }
    if (title.indexOf(' | ') === -1) {
      title = `${title} | National Geographic`;
    }
    if (removeSuffix) {
      title = title.replace(/ \|[^|]*$/i, '');
    }
    return title;
  },

  getMainImageEntity: content => {
    if (content.promoImage?.[0]?.entity?.mediaImage) {
      return content.promoImage[0].entity;
    }
    if (content.image?.entity?.mediaImage) {
      return content.image.entity;
    }
    return null;
  },

  getDescription: content => {
    let description = '';
    if (content.metaTags?.description) {
      return stripTags(content.metaTags.description);
    }
    if (content.promoSummary?.value) {
      description = content.promoSummary.value;
    } else if (content.subHeadline) {
      description = content.subHeadline;
    } else if (content.synopsis?.value) {
      // For Shows.
      description = content.synopsis.value;
    } else if (content.images?.[0]?.entity?.caption) {
      // For photoGalleries.
      description = content.images[0].entity.caption;
    } else if (content.mainContent) {
      if (content.mainContent.length > 0) {
        content.mainContent.map(paragraph => {
          if (
            paragraph.entity?.type &&
            paragraph.entity.type === 'ParagraphContent' &&
            paragraph.entity.content?.value &&
            description === ''
          ) {
            description = paragraph.entity.content.value;
          }
          return null;
        });
      }
    }
    if (
      (description === '' || !description) &&
      content.promoImage?.[0]?.entity?.caption
    ) {
      description = content.promoImage[0].entity.caption;
    }
    if (description === '' || !description) {
      description = conf.defaultMetadataDescription;
    }
    if (description) {
      return truncateText(stripTags(description), 160);
    }
    return null;
  }
};

export const metaDataService = {
  getContentMetaData: content => {
    const title = metaDataServiceHelper.getMetaTitle(content);
    return {
      // Ensure that all contents on this site are of the same language
      language: conf.langcode,
      title,
      meta: metaDataService.getAllMeta(content),
      link: metaDataService.getLink(content)
    };
  },

  getAllMeta: content => {
    return metaDataService
      .getStandardMeta(content)
      .concat(metaDataService.getOpenGraphMeta(content))
      .concat(metaDataService.getTwitterMeta(content))
      .concat(metaDataService.getArticleMeta(content))
      .concat(metaDataService.getSponsoredMeta(content));
  },

  getStandardMeta: content => {
    const contentChangedIso = new Date(
      getContentModificationDate(content) * 1000
    )
      .toISOString()
      .replace('.000Z', 'Z');

    const standardMeta = [
      {
        name: 'title',
        content:
          content.metaTags?.title || metaDataServiceHelper.getMetaTitle(content)
      },
      {
        name: 'date',
        scheme: 'W3CDTF',
        content: contentChangedIso
      },
      {
        name: 'publicationDate',
        content: dateService.format(
          getContentModificationDate(content) * 1000,
          'd/m/Y'
        )
      },
      {
        name: 'robots',
        content: 'max-image-preview:large, max-snippet:-1, max-video-preview:-1'
      }
    ];
    const description = metaDataServiceHelper.getDescription(content);
    if (description) {
      standardMeta.push({
        name: 'description',
        content: description
      });
    }

    if (content.metaTags?.keywords) {
      standardMeta.push({
        name: 'keywords',
        content: content.metaTags.keywords
      });
    }

    if (content.primaryTaxonomy?.entity?.name) {
      standardMeta.push({
        name: 'localPrimaryTerm',
        content: content.primaryTaxonomy.entity.name
      });
    }

    if (content.primaryTaxonomy?.entity?.englishName) {
      standardMeta.push({
        name: 'englishPrimaryTerm',
        content: content.primaryTaxonomy.entity.englishName
      });
    }

    return standardMeta;
  },

  getOpenGraphMeta: content => {
    const openGraphMeta = [
      {
        property: 'fb:app_id',
        content: conf.metadataDefaults.fb_app_id
      },
      {
        property: 'og:type',
        content: getOpenGraphType(content.bundle)
      },
      {
        property: 'og:url',
        content: `${getDomain()}${content.url?.path}`
      },
      {
        property: 'og:title',
        content: metaDataServiceHelper.getMetaTitle(content, 'og:title', true)
      },
      {
        property: 'og:site_name',
        content: conf.siteName
      },
      {
        property: 'og:updated_time',
        content: new Date(getContentModificationDate(content) * 1000)
          .toISOString()
          .replace('.000Z', 'Z')
      }
    ];

    if (content.metaTags?.['og:description']) {
      openGraphMeta.push({
        property: 'og:description',
        content: stripTags(content.metaTags['og:description'])
      });
    } else {
      const description = metaDataServiceHelper.getDescription(content);
      if (description) {
        openGraphMeta.push({
          property: 'og:description',
          content: description
        });
      }
    }
    return openGraphMeta;
  },
  getOpenGraphMetaImage: content => {
    const openGraphMetaImage = [];
    const mainImageEntity = metaDataServiceHelper.getMainImageEntity(content);
    if (mainImageEntity) {
      // There are some images that doesn't exist on the filesystem but
      // are inside Drupal's database, and their dimensions are 0
      if (
        mainImageEntity.mediaImage.width &&
        mainImageEntity.mediaImage.height &&
        mainImageEntity.mediaImage.url
      ) {
        const desktopStyleData = imageResizerService.getResizedImage(
          mainImageEntity.mediaImage,
          breakpointService.getLargeDesktopBreakpoint().width,
          {
            position: mainImageEntity.focalPoint,
            quality: imageResizerService.getImageEntityQuality(
              mainImageEntity.quality
            )
          }
        );

        openGraphMetaImage.push({
          property: 'og:image',
          itemprop: 'image',
          'data-type': `default_${content.id}`,
          content: desktopStyleData.url
        });

        openGraphMetaImage.push({
          property: 'og:image:width',
          'data-type': `default_${content.id}`,
          content: desktopStyleData.width
        });

        openGraphMetaImage.push({
          property: 'og:image:height',
          'data-type': `default_${content.id}`,
          content: desktopStyleData.height
        });
        openGraphMetaImage.push({
          property: 'og:image:type',
          'data-type': `default_${content.id}`,
          content: mimeTypeService.getMimeType(desktopStyleData.url)
        });
        // Whatsapp Image
        const desktopStyleDataSmall = imageResizerService.getResizedImage(
          mainImageEntity.mediaImage,
          400,
          {
            position: mainImageEntity.focalPoint,
            aspectRatio: { width: 1, height: 1 },
            quality: 75
          }
        );

        openGraphMetaImage.push({
          property: 'og:image',
          itemprop: 'image',
          'data-type': `whatsapp_${content.id}`,
          content: desktopStyleDataSmall.url
        });

        openGraphMetaImage.push({
          property: 'og:image:width',
          'data-type': `whatsapp_${content.id}`,
          content: desktopStyleDataSmall.width
        });

        openGraphMetaImage.push({
          property: 'og:image:height',
          'data-type': `whatsapp_${content.id}`,
          content: desktopStyleDataSmall.height
        });

        openGraphMetaImage.push({
          property: 'og:image:type',
          'data-type': `whatsapp_${content.id}`,
          content: mimeTypeService.getMimeType(desktopStyleDataSmall.url)
        });
      }
    }
    return openGraphMetaImage;
  },
  getTwitterMeta: content => {
    const twitterMeta = [
      {
        property: 'twitter:title',
        content: metaDataServiceHelper.getMetaTitle(
          content,
          'twitter:title',
          true
        )
      },
      {
        property: 'twitter:site',
        content: conf.metadataDefaults.twitter_cards_site
      },
      {
        property: 'twitter:creator',
        content: conf.metadataDefaults.twitter_cards_creator
      },
      {
        property: 'twitter:url',
        content: `${getDomain()}${content.url?.path}`
      }
    ];
    const mainImageEntity = metaDataServiceHelper.getMainImageEntity(content);
    if (mainImageEntity) {
      twitterMeta.push({
        property: 'twitter:image',
        content: imageResizerService.getStyleUrl(
          mainImageEntity.mediaImage.url,
          breakpointService.getLargeDesktopBreakpoint().width,
          null,
          { position: mainImageEntity.focalPoint }
        )
      });

      twitterMeta.push({
        property: 'twitter:image:alt',
        content: metaDataServiceHelper.getMetaTitle(content, undefined, true)
      });
    } else {
      if (content.metaTags?.['twitter:image']) {
        twitterMeta.push({
          property: 'twitter:image',
          content: content.metaTags['twitter:image']
        });
      }
      if (content.metaTags?.['twitter:image:alt']) {
        twitterMeta.push({
          property: 'twitter:image:alt',
          content: content.metaTags['twitter:image:alt']
        });
      }
    }

    return twitterMeta;
  },

  getArticleMeta: content => {
    const articleMeta = [
      {
        property: 'article:author',
        content: conf.metadataDefaults.article_author
      },
      {
        property: 'article:modified_time',
        content: new Date(getContentModificationDate(content) * 1000)
          .toISOString()
          .replace('.000Z', 'Z')
      }
    ];

    if (content.created) {
      articleMeta.push({
        property: 'article:published_time',
        content: new Date(content.created * 1000)
          .toISOString()
          .replace('.000Z', 'Z')
      });
    }

    if (content.primaryTaxonomy?.entity?.name) {
      articleMeta.push({
        property: 'article:section',
        content: content.primaryTaxonomy.entity.name
      });
    }

    if (content.metaTags?.news_keywords) {
      const tags = content.metaTags.news_keywords.split(',');
      tags.forEach(tag => {
        articleMeta.push({
          property: 'article:tag',
          content: tag.trim()
        });
      });
    }

    return articleMeta;
  },

  getLink: content => {
    // Link definitions.
    const links = [
      {
        rel: 'canonical',
        href: `${getDomain()}${content.url?.path}`
      }
    ];

    const mainImageEntity = metaDataServiceHelper.getMainImageEntity(content);
    if (mainImageEntity) {
      links.push({
        rel: 'image_src',
        href: imageResizerService.getStyleUrl(
          mainImageEntity.mediaImage.url,
          breakpointService.getLargeDesktopBreakpoint().width,
          null,
          { position: mainImageEntity.focalPoint }
        )
      });
    }

    return links;
  },

  getSponsoredMeta: content => {
    return content.branded
      ? [
          {
            property: 'cr:sponsored'
          },
          {
            property: 'branded_content_campaign',
            content: content.brandedCampaign
          }
        ]
      : [];
  }
};
